import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { ClientSelectField, Icon } from '../../../components';
import { Client, Group, Project } from '../../../models';
import { Groups } from '../../../services';
import { RootState, useAppDispatch, useAppSelector, loadClients, loadGroups, loadProjects } from '../../../redux';

interface SignalGroupClientSelectProps {
    clientId?: string | null;
    clients: Client[];
    disabled?: boolean;
    onChange: (clientId: string | null) => void;
}

function SignalGroupClientSelect(props: SignalGroupClientSelectProps) {
    const { clientId, clients, disabled, onChange } = props;

    const selectedClient = useMemo(() => {
        if (!clientId)
            return undefined;
        return (clients || []).find((client: Client) => client._id === clientId);
    }, [clients, clientId]);

    return (
        <div className={disabled ? 'pointer-events-none opacity-50' : undefined}>
            <Formik
                initialValues={{ client: selectedClient || null }}
                enableReinitialize
                onSubmit={() => {}}
            >
                <ClientSelectField
                    name="client"
                    value={selectedClient}
                    onChange={(client?: Client) => onChange(client?._id || null)}
                    className="!mt-0"
                    selectedClassName="!bg-white dark:bg-gray-800 !text-gray-900 dark:text-gray-100"
                    searchInputClassName="!ring-gray-300 dark:ring-gray-600"
                />
            </Formik>
        </div>
    );
}

function SignalGroupsLanguageSection() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { groups } = useAppSelector((state: RootState) => state.groups);
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { clients } = useAppSelector((state: RootState) => state.clients);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addGroupId, setAddGroupId] = useState('');
    const [addProjectId, setAddProjectId] = useState('');
    const [addClientId, setAddClientId] = useState('');

    useEffect(() => {
        dispatch(loadGroups());
        dispatch(loadProjects());
        dispatch(loadClients());
    }, [dispatch]);

    const namedGroups = useMemo(() => {
        return (groups || [])
            .filter((group: Group) => !!group.name)
            .slice()
            .sort((a: Group, b: Group) => a.name.localeCompare(b.name));
    }, [groups]);

    const configuredGroups = useMemo(
        () => namedGroups.filter((group: Group) => !!group.defaultProject),
        [namedGroups]
    );

    const availableGroups = useMemo(
        () => namedGroups.filter((group: Group) => !group.defaultProject),
        [namedGroups]
    );

    const projectOptions = useMemo(() => {
        const list = (projects || [])
            .slice()
            .sort((a: Project, b: Project) => a.name.localeCompare(b.name));
        return list.map((project: Project) => ({
            value: project._id,
            label: project.name,
        }));
    }, [projects]);

    const syncGroups = async () => {
        setSyncing(true);
        setError(null);
        try {
            await Groups.syncGroups();
            dispatch(loadGroups());
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSyncing(false);
        }
    };

    const patchGroup = async (
        groupId: string,
        patch: {
            notificationLanguage?: string | null;
            defaultProject?: string | null;
            defaultClient?: string | null;
        }
    ) => {
        setSavingId(groupId);
        setError(null);
        try {
            await Groups.patchGroup(groupId, patch);
            dispatch(loadGroups());
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSavingId(null);
        }
    };

    const addConfiguredGroup = async () => {
        if (!addGroupId || !addProjectId)
            return;
        setAdding(true);
        setError(null);
        try {
            await Groups.patchGroup(addGroupId, {
                defaultProject: addProjectId,
                ...(addClientId ? { defaultClient: addClientId } : {}),
            });
            setAddGroupId('');
            setAddProjectId('');
            setAddClientId('');
            dispatch(loadGroups());
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setAdding(false);
        }
    };

    const selectClassName = "block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

    return (
        <div className="mt-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('settings.signal_groups_language_title')}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('settings.signal_groups_language_desc')}
                    </p>
                </div>
                <button
                    type="button"
                    className="inline-flex shrink-0 items-center justify-center rounded-md bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={syncing}
                    onClick={syncGroups}
                >
                    {syncing ? t('settings.signal_syncing') : t('settings.signal_groups_sync')}
                </button>
            </div>
            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}

            <div className="mt-4 rounded border p-3">
                <p className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('settings.signal_groups_add')}
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('settings.signal_groups_select')}
                        </label>
                        <select
                            className={selectClassName}
                            value={addGroupId}
                            disabled={adding || availableGroups.length === 0}
                            onChange={(event) => setAddGroupId(event.target.value)}
                        >
                            <option value="">{t('settings.signal_groups_select_placeholder')}</option>
                            {availableGroups.map((group: Group) => (
                                <option key={group._id} value={group._id}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('settings.signal_groups_default_project')}
                        </label>
                        <select
                            className={selectClassName}
                            value={addProjectId}
                            disabled={adding}
                            onChange={(event) => setAddProjectId(event.target.value)}
                        >
                            <option value="">{t('settings.signal_groups_default_project_none')}</option>
                            {projectOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('settings.signal_groups_default_client')}
                        </label>
                        <SignalGroupClientSelect
                            clientId={addClientId || null}
                            clients={clients || []}
                            disabled={adding}
                            onChange={(clientId) => setAddClientId(clientId || '')}
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            type="button"
                            className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={adding || !addGroupId || !addProjectId}
                            onClick={addConfiguredGroup}
                        >
                            {t('settings.signal_groups_add_button')}
                        </button>
                    </div>
                </div>
            </div>

            {configuredGroups.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('settings.signal_groups_empty')}</p>
            ) : (
                <div className="mt-4 space-y-3">
                    {configuredGroups.map((group: Group) => (
                        <div key={group._id} className="grid grid-cols-1 gap-3 rounded border p-3 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                <Icon type="signal" color="blue-600" className="inline"/>
                                <span>{group.name}</span>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {t('settings.signal_groups_language_placeholder')}
                                </label>
                                <select
                                    className={selectClassName}
                                    value={group.notificationLanguage || ''}
                                    disabled={savingId === group._id}
                                    onChange={(event) => patchGroup(group._id, {
                                        notificationLanguage: event.target.value || null,
                                    })}
                                >
                                    <option value="">{t('settings.signal_groups_language_project_default')}</option>
                                    <option value="fr">{t('common.languages.fr')}</option>
                                    <option value="en">{t('common.languages.en')}</option>
                                    <option value="de">{t('common.languages.de')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {t('settings.signal_groups_default_project')}
                                </label>
                                <select
                                    className={selectClassName}
                                    value={group.defaultProject || ''}
                                    disabled={savingId === group._id}
                                    onChange={(event) => patchGroup(group._id, {
                                        defaultProject: event.target.value || null,
                                    })}
                                >
                                    <option value="">{t('settings.signal_groups_default_project_none')}</option>
                                    {projectOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {t('settings.signal_groups_default_client')}
                                </label>
                                <SignalGroupClientSelect
                                    clientId={group.defaultClient}
                                    clients={clients || []}
                                    disabled={savingId === group._id}
                                    onChange={(clientId) => patchGroup(group._id, {
                                        defaultClient: clientId,
                                    })}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    className="inline-flex w-full items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                                    disabled={savingId === group._id}
                                    onClick={() => patchGroup(group._id, {
                                        defaultProject: null,
                                        defaultClient: null,
                                    })}
                                >
                                    {t('settings.signal_groups_remove')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SignalGroupsLanguageSection;
